import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Beef, ArrowLeft, ShoppingBag, Plus, Minus, Trash2, 
  Phone, User, Calendar, Clock, MessageSquare, Check, 
  ChevronRight, Package, AlertCircle, RefreshCw, ChevronUp, X, Search, ImageOff, Truck, Store, MapPin, Loader2, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useCart } from '@/context/CartContext';
import PushNotificationManager from '@/components/PushNotificationManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;


function ProductImage({ product, primaryColor, showImages }) {
  const [failed, setFailed] = useState(false);
  const icons = {
    pollo: '🐔', vitella: '🥩', maiale: '🐖', preparati: '👨‍🍳',
    pane: '🥖', vino: '🍷', uova: '🥚', salame: '🥓'
  };
  if (!showImages) return null;
  if (product.image_url && !failed) {
    return (
      <img
        src={product.image_url}
        alt={product.name}
        onError={() => setFailed(true)}
        className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-l-xl flex-shrink-0"
        loading="lazy"
      />
    );
  }
  return (
    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-l-xl flex-shrink-0 flex flex-col items-center justify-center text-white"
      style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}bb)` }}>
      <span className="text-3xl mb-0.5">{icons[product.category] || '🥩'}</span>
      <span className="hidden text-[10px] font-semibold px-2 text-center opacity-90">{product.name}</span>
      <ImageOff className="w-4 h-4 mt-2 opacity-50" />
    </div>
  );
}

const quantityToGrams = quantity => Math.round((Number(quantity) || 0) * 1000);
const gramsToQuantity = grams => Number((grams / 1000).toFixed(1));

const formatQuantity = (quantity, unit) => {
  const numeric = Number(quantity) || 0;

  if (unit === 'kg') {
    const grams = Math.min(50000, Math.max(0, Math.round(quantityToGrams(numeric) / 100) * 100));
    if (grams < 1000) return `${grams} g`;

    const wholeKilograms = Math.floor(grams / 1000);
    const hundreds = Math.floor((grams % 1000) / 100);
    return hundreds === 0 ? `${wholeKilograms} kg` : `${wholeKilograms},${hundreds} kg`;
  }

  const amount = Math.min(100, Math.max(0, Math.round(numeric)));
  const labels = {
    pz: amount === 1 ? 'pezzo' : 'pezzi',
    conf: amount === 1 ? 'confezione' : 'confezioni',
    bottiglia: amount === 1 ? 'bottiglia' : 'bottiglie'
  };
  return `${amount} ${labels[unit] || unit}`;
};

const parseWrittenQuantity = (rawValue, unit, allowZero = false) => {
  const original = String(rawValue ?? '').trim().toLocaleLowerCase('it-IT');
  if (!original) return null;
  const cleaned = original.replace(',', '.');

  if (unit === 'kg') {
    const match = cleaned.match(/\d+(?:\.\d+)?/);
    if (!match) return null;

    const numeric = Number.parseFloat(match[0]);
    if (!Number.isFinite(numeric)) return null;

    const writtenInGrams = cleaned.includes('g') && !cleaned.includes('kg');
    const rawGrams = writtenInGrams ? numeric : numeric * 1000;
    const grams = Math.min(50000, Math.max(allowZero ? 0 : 100, Math.round(rawGrams / 100) * 100));
    return gramsToQuantity(grams);
  }

  const numeric = Number.parseInt(cleaned.replace(/[^0-9]/g, ''), 10);
  if (!Number.isFinite(numeric)) return null;
  return Math.min(100, Math.max(allowZero ? 0 : 1, numeric));
};


const toItalianNumber = value => String(value).replace('.', ',');

const quantityDraftFromItem = item => {
  if (item.unit !== 'kg') {
    return { amount: String(Math.max(1, Math.round(Number(item.quantity) || 1))), measure: item.unit };
  }
  const grams = quantityToGrams(item.quantity);
  if (grams < 1000) return { amount: String(grams), measure: 'g' };
  return { amount: toItalianNumber((grams / 1000).toFixed(1).replace(/\.0$/, '')), measure: 'kg' };
};

const quantityFromNumericInput = (amount, measure, productUnit, allowZero = false) => {
  const normalized = String(amount ?? '').trim().replace(',', '.');
  const numeric = Number(normalized);
  if (!Number.isFinite(numeric)) return null;

  if (productUnit === 'kg') {
    const grams = measure === 'g' ? numeric : numeric * 1000;
    const min = allowZero ? 0 : 100;
    const rounded = Math.min(50000, Math.max(min, Math.round(grams / 100) * 100));
    return gramsToQuantity(rounded);
  }

  return Math.min(100, Math.max(allowZero ? 0 : 1, Math.round(numeric)));
};

function EditableQuantity({ item, updateQuantity, changeQuantityByStep, compact = false }) {
  const initial = quantityDraftFromItem(item);
  const [amount, setAmount] = useState(initial.amount);
  const [measure, setMeasure] = useState(initial.measure);

  useEffect(() => {
    const next = quantityDraftFromItem(item);
    setAmount(next.amount);
    setMeasure(next.measure);
  }, [item.quantity, item.unit]);

  const commit = () => {
    const parsed = quantityFromNumericInput(amount, measure, item.unit, true);
    if (parsed == null) {
      const current = quantityDraftFromItem(item);
      setAmount(current.amount);
      setMeasure(current.measure);
      return;
    }
    updateQuantity(item.cart_item_id || item.product_id, parsed);
  };

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => changeQuantityByStep(item.cart_item_id || item.product_id, -1)}
        className={`${compact ? 'w-8 h-8' : 'w-9 h-9'} rounded-lg bg-[#F3F0EA] flex items-center justify-center flex-shrink-0`}
        aria-label={item.unit === 'kg' ? 'Diminuisci di 100 grammi' : 'Diminuisci quantità'}
      >
        <Minus className="w-4 h-4" />
      </button>

      <input
        type="text"
        inputMode={item.unit === 'kg' && measure === 'kg' ? 'decimal' : 'numeric'}
        pattern={item.unit === 'kg' && measure === 'kg' ? '[0-9]*[.,]?[0-9]*' : '[0-9]*'}
        value={amount}
        onFocus={event => event.currentTarget.select()}
        onChange={event => setAmount(event.target.value.replace(/[^0-9.,]/g, ''))}
        onBlur={commit}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
            event.currentTarget.blur();
          }
        }}
        className={`${compact ? 'w-16 h-8 text-sm' : 'w-20 h-9 text-base'} px-2 text-center font-bold border border-[#D8D2C8] rounded-lg bg-white focus:outline-none focus:ring-2`}
        aria-label={item.unit === 'kg' ? 'Peso numerico' : 'Quantità numerica'}
      />

      {item.unit === 'kg' && (
        <select
          value={measure}
          onChange={event => {
            const nextMeasure = event.target.value;
            const parsed = quantityFromNumericInput(amount, measure, item.unit, true) ?? 0;
            const grams = quantityToGrams(parsed);
            setMeasure(nextMeasure);
            setAmount(nextMeasure === 'g'
              ? String(grams)
              : toItalianNumber((grams / 1000).toFixed(1).replace(/\.0$/, ''))
            );
          }}
          className={`${compact ? 'h-8' : 'h-9'} px-2 rounded-lg border border-[#D8D2C8] bg-white text-sm font-semibold`}
          aria-label="Unità di peso"
        >
          <option value="g">g</option>
          <option value="kg">kg</option>
        </select>
      )}

      <button
        type="button"
        onClick={() => changeQuantityByStep(item.cart_item_id || item.product_id, 1)}
        disabled={item.unit === 'kg' ? Number(item.quantity) >= 50 : Number(item.quantity) >= 100}
        className={`${compact ? 'w-8 h-8' : 'w-9 h-9'} rounded-lg text-white flex items-center justify-center flex-shrink-0 disabled:opacity-40`}
        style={{ backgroundColor: 'var(--quantity-primary, #5D1822)' }}
        aria-label={item.unit === 'kg' ? 'Aumenta di 100 grammi' : 'Aumenta quantità'}
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}

function ProductQuantityPicker({ product, draftKey, value, onChange, primaryColor }) {
  const productUnit = product.unit || 'kg';
  const initialParsed = parseWrittenQuantity(value ?? (productUnit === 'kg' ? '500 g' : '1'), productUnit, true)
    ?? (productUnit === 'kg' ? 0.5 : 1);
  const initialGrams = productUnit === 'kg' ? quantityToGrams(initialParsed) : 0;

  const [measure, setMeasure] = useState(productUnit === 'kg' && initialGrams >= 1000 ? 'kg' : (productUnit === 'kg' ? 'g' : productUnit));
  const [amount, setAmount] = useState(
    productUnit === 'kg'
      ? (initialGrams >= 1000
          ? toItalianNumber((initialGrams / 1000).toFixed(1).replace(/\.0$/, ''))
          : String(initialGrams))
      : String(initialParsed)
  );

  useEffect(() => {
    if (value == null) return;
    const parsed = parseWrittenQuantity(value, productUnit, true);
    if (parsed == null) return;
    if (productUnit === 'kg') {
      const grams = quantityToGrams(parsed);
      const nextMeasure = String(value).toLowerCase().includes('kg') || grams >= 1000 ? 'kg' : 'g';
      setMeasure(nextMeasure);
      setAmount(nextMeasure === 'g'
        ? String(grams)
        : toItalianNumber((grams / 1000).toFixed(1).replace(/\.0$/, ''))
      );
    } else {
      setAmount(String(parsed));
    }
  }, [value, productUnit]);

  const publish = (nextAmount, nextMeasure = measure) => {
    const parsed = quantityFromNumericInput(nextAmount, nextMeasure, productUnit, true);
    if (parsed == null) return;
    onChange(draftKey, formatQuantity(parsed, productUnit));
  };

  const commit = () => {
    const parsed = quantityFromNumericInput(amount, measure, productUnit, true);
    if (parsed == null) {
      setAmount(productUnit === 'kg' ? '500' : '1');
      setMeasure(productUnit === 'kg' ? 'g' : productUnit);
      publish(productUnit === 'kg' ? '500' : '1', productUnit === 'kg' ? 'g' : productUnit);
      return;
    }
    const grams = productUnit === 'kg' ? quantityToGrams(parsed) : 0;
    setAmount(productUnit === 'kg'
      ? (measure === 'g'
          ? String(grams)
          : toItalianNumber((grams / 1000).toFixed(1).replace(/\.0$/, '')))
      : String(parsed)
    );
    publish(amount, measure);
  };

  const changeByStep = direction => {
    const parsed = quantityFromNumericInput(amount, measure, productUnit, true) ?? 0;
    if (productUnit === 'kg') {
      const nextGrams = Math.min(50000, Math.max(0, quantityToGrams(parsed) + direction * 100));
      const nextAmount = measure === 'g'
        ? String(nextGrams)
        : toItalianNumber((nextGrams / 1000).toFixed(1).replace(/\.0$/, ''));
      setAmount(nextAmount);
      publish(nextAmount, measure);
      return;
    }

    const nextAmount = String(Math.min(100, Math.max(0, Math.round(parsed) + direction)));
    setAmount(nextAmount);
    publish(nextAmount, measure);
  };

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => changeByStep(-1)}
        className="w-8 h-8 rounded-lg bg-[#F3F0EA] flex items-center justify-center flex-shrink-0"
      >
        <Minus className="w-4 h-4" />
      </button>

      <input
        type="text"
        inputMode={productUnit === 'kg' && measure === 'kg' ? 'decimal' : 'numeric'}
        pattern={productUnit === 'kg' && measure === 'kg' ? '[0-9]*[.,]?[0-9]*' : '[0-9]*'}
        value={amount}
        onFocus={event => event.currentTarget.select()}
        onChange={event => setAmount(event.target.value.replace(/[^0-9.,]/g, ''))}
        onBlur={commit}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
            event.currentTarget.blur();
          }
        }}
        className="w-16 h-8 px-2 text-center text-sm font-bold border border-[#D8D2C8] rounded-lg bg-white focus:outline-none focus:ring-2"
        style={{ '--tw-ring-color': primaryColor }}
        aria-label={productUnit === 'kg' ? 'Peso numerico' : 'Quantità numerica'}
      />

      {productUnit === 'kg' && (
        <select
          value={measure}
          onChange={event => {
            const parsed = quantityFromNumericInput(amount, measure, productUnit, true) ?? 0;
            const grams = quantityToGrams(parsed);
            const nextMeasure = event.target.value;
            const nextAmount = nextMeasure === 'g'
              ? String(grams)
              : toItalianNumber((grams / 1000).toFixed(1).replace(/\.0$/, ''));
            setMeasure(nextMeasure);
            setAmount(nextAmount);
            publish(nextAmount, nextMeasure);
          }}
          className="h-8 px-2 rounded-lg border border-[#D8D2C8] bg-white text-sm font-semibold"
          aria-label="Unità di peso"
        >
          <option value="g">g</option>
          <option value="kg">kg</option>
        </select>
      )}

      <button
        type="button"
        onClick={() => changeByStep(1)}
        disabled={(quantityFromNumericInput(amount, measure, productUnit, true) ?? 0) >= (productUnit === 'kg' ? 50 : 100)}
        className="w-8 h-8 rounded-lg text-white flex items-center justify-center flex-shrink-0 disabled:opacity-40"
        style={{ backgroundColor: primaryColor }}
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function OrderPage({ checkoutOnly = false }) {
  const location = useLocation();
  const { cart, addToCart, updateQuantity, changeQuantityByStep, removeFromCart, updateItemNotes, clearCart, refreshCart, cartTotal, isInitialized, setRoundingStep, roundMoney } = useCart();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [weightVariationAccepted, setWeightVariationAccepted] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [submittedOrder, setSubmittedOrder] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);

  // Ogni volta che si apre la pagina Ordina, mostra sempre prima le categorie.
  // Evita che resti aperta una categoria selezionata in una visita precedente.
  useEffect(() => {
    if (!checkoutOnly && location.pathname === '/ordina') {
      setActiveCategory(null);
      setSearchTerm('');
      setSelectedProduct(null);
    }
  }, [checkoutOnly, location.pathname]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productAdded, setProductAdded] = useState(false);
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [productQuantityDrafts, setProductQuantityDrafts] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [deliveryValidation, setDeliveryValidation] = useState({
    checking: false,
    valid: false,
    message: '',
    formattedAddress: ''
  });
  const [showDeliveryNotice, setShowDeliveryNotice] = useState(false);
  
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    pickup_date: '',
    pickup_time: '',
    fulfillment_type: 'pickup',
    delivery_zone_id: '',
    delivery_address: '',
    delivery_notes: '',
    notes: ''
  });


  useEffect(() => {
    let cancelled = false;

    const applyUser = user => {
      if (!user || cancelled) return;
      setCustomerInfo(previous => ({
        ...previous,
        name: previous.name || user.full_name || user.name || '',
        phone: previous.phone || user.phone || '',
        email: previous.email || user.email || '',
        delivery_address: previous.delivery_address || user.address || user.delivery_address || ''
      }));
    };

    try {
      const savedUser = JSON.parse(localStorage.getItem('user') || 'null');
      applyUser(savedUser);
    } catch (error) {
      console.warn('Dati utente locali non leggibili:', error);
    }

    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(response => {
        applyUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      }).catch(() => {
        // L'ordine resta disponibile anche senza sessione autenticata.
      });
    }

    return () => {
      cancelled = true;
    };
  }, []);

  const configuredCategories = Array.isArray(settings?.product_categories) && settings.product_categories.length
    ? settings.product_categories
    : [
        { id: 'vitella', name: 'Vitella', icon: '🥩', visible: true, order: 0 },
        { id: 'maiale', name: 'Maiale', icon: '🐖', visible: true, order: 1 },
        { id: 'pollo', name: 'Pollo', icon: '🐔', visible: true, order: 2 },
        { id: 'preparati', name: 'Preparati', icon: '🍔', visible: true, order: 3 },
        { id: 'altro', name: 'Altro', icon: '🛒', visible: true, order: 4 }
      ];

  const visibleCategories = configuredCategories
    .map((item, index) => ({
      ...item,
      visible: item.visible !== false,
      order: Number.isFinite(Number(item.order)) ? Number(item.order) : index
    }))
    .filter(item => item.visible)
    .sort((a, b) => a.order - b.order);

  const categories = visibleCategories;


  const normalizeCategory = (product) => {
    const category = product?.category;
    const allowed = configuredCategories.map(item => item.id);
    if (allowed.includes(category)) return category;
    if (category === 'vini-bevande') return 'vino';
    if (category === 'salumi') return 'salame';
    if (category === 'pane-uova') {
      return String(product?.name || '').toLocaleLowerCase('it-IT').includes('uova') ? 'uova' : 'pane';
    }
    if (category === 'carne') {
      const name = String(product?.name || '').toLocaleLowerCase('it-IT');
      if (/pollo|petto|cosce|sovracosce|fusi|ali/.test(name)) return 'pollo';
      if (/maiale|salsiccia|pancetta|lonza|arista|costine|stinco|capocollo/.test(name)) return 'maiale';
      return 'vitella';
    }
    return visibleCategories[0]?.id || 'preparati';
  };

  const categoryDescriptions = {
    pollo: 'Pollo fresco, tagli e preparazioni.',
    vitella: 'Tagli freschi di vitella.',
    maiale: 'Carne di maiale fresca.',
    preparati: 'Prodotti pronti da cuocere preparati ogni giorno.',
    pane: 'Pane, panini e mollica.',
    vino: 'Vini disponibili in macelleria.',
    uova: 'Uova fresche in confezione.',
    salame: 'Salame e prodotti della categoria.'
  };

  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setRoundingStep(settings?.price_rounding_step ?? 0.10);
  }, [settings?.price_rounding_step, setRoundingStep]);

  // Dynamically load the fonts chosen for the order page
  useEffect(() => {
    if (!settings) return;
    const heading = settings.order_heading_font || 'Playfair Display';
    const body = settings.order_body_font || 'Inter';
    const families = [heading, body].filter((v, i, a) => a.indexOf(v) === i);
    const href = 'https://fonts.googleapis.com/css2?' +
      families.map(f => 'family=' + f.trim().replace(/ /g, '+') + ':wght@400;500;600;700').join('&') +
      '&display=swap';
    let link = document.getElementById('order-page-fonts');
    if (!link) {
      link = document.createElement('link');
      link.id = 'order-page-fonts';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = href;
  }, [settings]);

  const fetchData = async () => {
    setError(null);
    try {
      // Aggiungi timestamp per evitare cache
      const timestamp = Date.now();
      const [productsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/products?t=${timestamp}`, {
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }),
        axios.get(`${API}/settings?t=${timestamp}`, {
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        })
      ]);
      // Filter only available products
      const availableProducts = productsRes.data.filter(p => p.available !== false);
      console.log('Products loaded:', availableProducts.length);
      setProducts(availableProducts);
      setSettings(settingsRes.data);
    } catch (err) {
      console.error('Errore caricamento dati:', err);
      setError('Impossibile caricare i prodotti. Verifica la connessione e riprova.');
    } finally {
      setLoading(false);
    }
  };

  // Funzione per aggiornare tutto (prodotti + carrello)
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchData();
      if (refreshCart) {
        await refreshCart();
      }
      toast.success('Dati aggiornati!');
    } catch (err) {
      toast.error('Errore durante l\'aggiornamento');
    } finally {
      setRefreshing(false);
    }
  };

  const handleAddToCart = (product) => {
    const variants = (product.variants || []).filter(variant => variant.available !== false);
    const selected = variants.find(variant => variant.id === selectedVariants[product.id]) || null;
    if (variants.length && !selected) {
      toast.error('Seleziona una variante');
      return false;
    }

    const draftKey = `${product.id}::${selected?.id || 'base'}`;
    const defaultValue = product.unit === 'kg' ? '500 g' : '1 pezzo';
    const requestedQuantity = parseWrittenQuantity(
      productQuantityDrafts[draftKey] ?? defaultValue,
      product.unit,
      true
    );

    if (requestedQuantity == null || requestedQuantity <= 0) {
      toast.error(product.unit === 'kg' ? 'Scegli almeno 100 g' : 'Scegli almeno 1 pezzo');
      return false;
    }

    addToCart(product, requestedQuantity, selected);
    toast.success(`${product.name}${selected ? ` — ${selected.name}` : ''} aggiunto al carrello`);
    return true;
  };

  const getSelectedSchedule = () => (
    customerInfo.fulfillment_type === 'delivery'
      ? (settings?.delivery_hours || {})
      : (settings?.order_hours || settings?.opening_hours || {})
  );

  const getAvailablePickupTimes = () => {
    const schedule = getSelectedSchedule();
    if (!customerInfo.pickup_date) return [];
    try {
      const date = new Date(customerInfo.pickup_date + 'T12:00:00');
      const dayMap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
      const hours = schedule[dayMap[date.getDay()]] || {};
      const result = [];
      const addRange = (startValue, endValue) => {
        if (!startValue || !endValue) return;
        let [h, m] = startValue.split(':').map(Number);
        const [eh, em] = endValue.split(':').map(Number);
        const endTotal = eh * 60 + em;
        while (h * 60 + m < endTotal) {
          result.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
          m += 30;
          if (m >= 60) { h += 1; m = 0; }
        }
      };
      addRange(hours.open, hours.close);
      addRange(hours.afternoon_open, hours.afternoon_close);

      // Per oggi mostra soltanto gli orari non ancora passati.
      const now = new Date();
      const selectedDate = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0')
      ].join('-');

      if (customerInfo.pickup_date === selectedDate) {
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        return result.filter(time => {
          const [hour, minute] = time.split(':').map(Number);
          return hour * 60 + minute > currentMinutes;
        });
      }

      return result;
    } catch {
      return [];
    }
  };

  const isWorkingDay = (dateString) => {
    const schedule = getSelectedSchedule();
    const date = new Date(dateString + 'T12:00:00');
    const dayMap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const hours = schedule[dayMap[date.getDay()]] || {};
    return Boolean(
      (hours.open && hours.close)
      || (hours.afternoon_open && hours.afternoon_close)
    );
  };

  const formatLocalDate = (date) => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-');

  const getNextWorkingDay = () => {
    const today = new Date();

    // Parte da oggi, non da domani.
    for (let index = 0; index <= 30; index += 1) {
      const next = new Date(today);
      next.setDate(today.getDate() + index);
      const value = formatLocalDate(next);

      if (!isWorkingDay(value)) continue;

      if (index === 0) {
        const previousDate = customerInfo.pickup_date;
        const schedule = getSelectedSchedule();
        const dayMap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
        const hours = schedule[dayMap[next.getDay()]] || {};
        const ranges = [
          [hours.open, hours.close],
          [hours.afternoon_open, hours.afternoon_close]
        ];

        const nowMinutes = today.getHours() * 60 + today.getMinutes();
        const hasFutureSlot = ranges.some(([startValue, endValue]) => {
          if (!startValue || !endValue) return false;
          const [endHour, endMinute] = endValue.split(':').map(Number);
          return endHour * 60 + endMinute > nowMinutes;
        });

        if (!hasFutureSlot) continue;
      }

      return value;
    }
    return '';
  };

  useEffect(() => {
    setCustomerInfo(prev => ({
      ...prev,
      pickup_date: getNextWorkingDay(),
      pickup_time: ''
    }));
  }, [
    customerInfo.fulfillment_type,
    settings?.order_hours,
    settings?.opening_hours,
    settings?.delivery_hours
  ]);

  const validateDeliveryAddress = async ({ silent = false } = {}) => {
    const address = customerInfo.delivery_address.trim();
    if (activeDeliveryZones.length && !selectedDeliveryZone) {
      if (!silent) toast.error('Seleziona prima il paese di consegna');
      return null;
    }
    if (!address) {
      if (!silent) toast.error('Inserisci via, numero civico e comune');
      return null;
    }

    setDeliveryValidation(prev => ({ ...prev, checking: true, message: '' }));
    try {
      const response = await axios.post(`${API}/validate-delivery-address`, {
        address,
        delivery_zone_id: customerInfo.delivery_zone_id || null
      });
      const result = response.data || {};
      setDeliveryValidation({
        checking: false,
        valid: Boolean(result.valid),
        message: result.message || '',
        formattedAddress: result.formatted_address || address
      });
      if (result.valid && result.formatted_address) {
        setCustomerInfo(prev => ({ ...prev, delivery_address: result.formatted_address }));
      }
      if (!silent) {
        result.valid ? toast.success(result.message || 'Indirizzo verificato') : toast.error(result.message || 'Indirizzo non valido');
      }
      return result;
    } catch (error) {
      const message = error.response?.data?.detail || 'Impossibile verificare l’indirizzo';
      setDeliveryValidation({ checking: false, valid: false, message, formattedAddress: '' });
      if (!silent) toast.error(message);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cart.length === 0) {
      toast.error('Il carrello è vuoto');
      return;
    }

    const missingFields = [];
    if (!customerInfo.name?.trim()) missingFields.push('nome e cognome');
    if (!customerInfo.phone?.trim()) missingFields.push('numero di telefono');
    if (!customerInfo.pickup_date) missingFields.push('giorno');
    if (!customerInfo.pickup_time) missingFields.push('orario');

    if (missingFields.length > 0) {
      toast.error(`Per continuare manca: ${missingFields.join(', ')}`);
      return;
    }

    if (customerInfo.fulfillment_type === 'delivery') {
      if (activeDeliveryZones.length && !selectedDeliveryZone) {
        toast.error('Per continuare manca: paese di consegna');
        return;
      }
      if (!customerInfo.delivery_address.trim()) {
        toast.error('Per continuare manca: via, numero civico e comune');
        return;
      }
    }

    // Validate minimum order
    const minOrder = customerInfo.fulfillment_type === 'delivery'
      ? (settings?.delivery_min_order_amount || 20)
      : (settings?.min_order_amount || 10);
    if (cartTotal < minOrder) {
      toast.error(`Ordine minimo €${minOrder.toFixed(2).replace('.', ',')}: aggiungi ancora €${roundMoney(minOrder - cartTotal).toFixed(2).replace('.', ',')}`);
      return;
    }

    if (!weightVariationAccepted) {
      toast.error('Per continuare devi spuntare l’avviso su peso e prezzo');
      return;
    }
    if (customerInfo.fulfillment_type === 'delivery') {
      const addressResult = await validateDeliveryAddress({ silent: true });
      if (!addressResult?.valid) {
        toast.error(addressResult?.message || 'Controlla l’indirizzo di consegna');
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/orders`, {
        customer_name: customerInfo.name,
        customer_phone: customerInfo.phone,
        customer_email: customerInfo.email,
        items: cart,
        pickup_date: customerInfo.pickup_date,
        pickup_time: customerInfo.pickup_time,
        fulfillment_type: customerInfo.fulfillment_type,
        delivery_zone_id: customerInfo.delivery_zone_id,
        delivery_address: customerInfo.delivery_address,
        delivery_notes: customerInfo.delivery_notes,
        notes: customerInfo.notes
      });
      
      setSubmittedOrder({
        ...(response.data || {}),
        total: finalTotal,
        items_count: cart.length,
        fulfillment_type: customerInfo.fulfillment_type,
        date: customerInfo.pickup_date,
        time: customerInfo.pickup_time,
        address: customerInfo.delivery_address
      });
      setOrderSuccess(true);
      clearCart();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Errore nell\'invio dell\'ordine';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const normalizedSearch = searchTerm.trim().toLocaleLowerCase('it-IT');
  const categoryOrder = { vitella: 0, maiale: 1, pollo: 2, preparati: 3, altro: 4 };
  const filteredProducts = products
    .map(product => ({ ...product, display_category: normalizeCategory(product) }))
    .filter(product => !activeCategory || product.display_category === activeCategory)
    .filter(product => {
      if (!normalizedSearch) return true;
      const variantsText = (product.variants || []).map(variant => variant.name).join(' ');
      return `${product.name} ${product.description || ''} ${variantsText}`
        .toLocaleLowerCase('it-IT')
        .includes(normalizedSearch);
    })
    .sort((a, b) =>
      (categoryOrder[a.display_category] ?? 99) - (categoryOrder[b.display_category] ?? 99)
      || (a.sort_order ?? 999) - (b.sort_order ?? 999)
      || a.name.localeCompare(b.name, 'it-IT')
    );

  // Oggi è selezionabile; gli orari passati vengono filtrati separatamente.
  const minDate = formatLocalDate(new Date());

  const primaryColor = settings?.order_primary_color || settings?.primary_color || '#5D1822';
  const orderBg = settings?.order_bg_color || '#F9F7F2';
  const orderTextColor = settings?.order_text_color || '#2C2C2C';
  const headingFont = settings?.order_heading_font || 'Playfair Display';
  const bodyFont = settings?.order_body_font || 'Inter';
  const orderTitle = settings?.order_title || 'Prenota il Tuo Ordine';
  const orderSubtitle = settings?.order_subtitle || 'Scegli i prodotti e ritirali in negozio';
  const showCategories = settings?.order_show_categories !== false;
  const showImages = settings?.order_show_images !== false;
  const showNotes = settings?.order_show_notes !== false;
  const minOrder = customerInfo.fulfillment_type === 'delivery'
    ? (settings?.delivery_min_order_amount || 20)
    : (settings?.min_order_amount || 10);
  const activeDeliveryZones = (settings?.delivery_zones || []).filter(zone => zone && zone.enabled !== false && String(zone.name || '').trim());
  const selectedDeliveryZone = activeDeliveryZones.find(zone => String(zone.id) === String(customerInfo.delivery_zone_id));
  const deliveryFee = customerInfo.fulfillment_type === 'delivery'
    ? Number(selectedDeliveryZone?.fee ?? settings?.delivery_fee ?? 0)
    : 0;
  const finalTotal = roundMoney((cartTotal || 0) + deliveryFee);
  const availableTimes = getAvailablePickupTimes();

  // Non mostrare dati o grafica predefiniti mentre vengono caricati
  // prodotti e impostazioni aggiornati dal server. In questo modo non appare
  // per alcuni secondi la vecchia versione della pagina Ordina.
  if (loading && !settings) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{ backgroundColor: '#F9F7F2' }}
      >
        <div className="text-center" role="status" aria-live="polite">
          <div
            className="w-12 h-12 mx-auto rounded-full border-4 border-[#E8E2D8] border-t-[#C9362B] animate-spin"
            aria-hidden="true"
          />
          <p className="mt-4 text-base font-semibold text-[#5A514A]">
            Caricamento categorie…
          </p>
        </div>
      </div>
    );
  }

  if (orderSuccess) {
    const confirmationDate = submittedOrder?.date || customerInfo.pickup_date;
    const confirmationTime = submittedOrder?.time || customerInfo.pickup_time;
    const confirmationTotal = Number(submittedOrder?.total || 0);
    const orderCode = submittedOrder?.order_number || submittedOrder?.order_code || submittedOrder?.id;

    return (
      <div className="min-h-screen bg-[#F9F7F2] px-4 py-8 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="px-6 pt-8 pb-6 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <p className="text-sm font-bold uppercase tracking-wider text-green-700 mb-2">Ordine inviato</p>
            <h2 className="text-3xl font-serif font-bold" style={{ color: primaryColor }}>
              Grazie, {customerInfo.name.split(' ')[0] || 'ordine ricevuto'}!
            </h2>
            <p className="text-[#5A5A5A] mt-3">Riceverai la conferma appena la macelleria prende in carico l’ordine.</p>
          </div>

          <div className="mx-5 mb-5 rounded-2xl bg-[#F9F7F2] border border-[#E8E2D8] divide-y divide-[#E8E2D8]">
            {orderCode && (
              <div className="p-4 flex justify-between gap-4">
                <span className="text-sm text-[#6B655D]">Numero ordine</span>
                <strong>#{String(orderCode).slice(-8).toUpperCase()}</strong>
              </div>
            )}
            <div className="p-4 flex justify-between gap-4">
              <span className="text-sm text-[#6B655D]">{customerInfo.fulfillment_type === 'delivery' ? 'Consegna' : 'Ritiro'}</span>
              <strong className="text-right">
                {confirmationDate ? new Date(`${confirmationDate}T12:00:00`).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' }) : ''}
                {confirmationTime ? ` alle ${confirmationTime}` : ''}
              </strong>
            </div>
            {customerInfo.fulfillment_type === 'delivery' && customerInfo.delivery_address && (
              <div className="p-4 flex justify-between gap-4">
                <span className="text-sm text-[#6B655D]">Indirizzo</span>
                <strong className="text-right">{customerInfo.delivery_address}</strong>
              </div>
            )}
            <div className="p-4 flex justify-between items-center gap-4">
              <span className="text-sm text-[#6B655D]">Totale</span>
              <strong className="text-xl" style={{ color: primaryColor }}>€{confirmationTotal.toFixed(2).replace('.', ',')}</strong>
            </div>
          </div>

          <div className="p-5 pt-0 grid gap-3">
            <Link to="/" className="w-full min-h-[52px] rounded-2xl text-white font-bold flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
              Torna alla Home
            </Link>
            <Link to="/ordina" className="w-full min-h-[50px] rounded-2xl border-2 font-bold flex items-center justify-center" style={{ borderColor: primaryColor, color: primaryColor }}>
              Fai un altro ordine
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-order-page style={{ backgroundColor: orderBg, color: orderTextColor, fontFamily: `'${bodyFont}', system-ui, sans-serif`, '--order-heading-font': `'${headingFont}', serif` }}>
      <style>{`[data-order-page] .font-serif{font-family:var(--order-heading-font) !important}`}</style>

      {showDeliveryNotice && (
        <div className="fixed inset-0 z-[100] bg-black/55 px-4 flex items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="delivery-notice-title">
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden">
            <div className="p-5 sm:p-6">
              <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center mb-4">
                <Truck className="w-7 h-7 text-blue-700" />
              </div>
              <h2 id="delivery-notice-title" className="text-2xl font-serif font-bold" style={{ color: primaryColor }}>Zone di consegna</h2>
              <p className="mt-2 text-[#5F5952]">Consegniamo soltanto nelle zone indicate. La tariffa viene aggiunta automaticamente all’ordine.</p>
              <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 divide-y divide-blue-200">
                {activeDeliveryZones.length > 0 ? activeDeliveryZones.map(zone => (
                  <div key={zone.id} className="flex items-center justify-between gap-4 px-4 py-3">
                    <strong>{zone.name}</strong>
                    <span className="font-bold text-blue-800">€{Number(zone.fee || 0).toFixed(2).replace('.', ',')}</span>
                  </div>
                )) : (
                  <p className="px-4 py-3 text-sm text-blue-900">Le zone disponibili verranno mostrate durante l’ordine.</p>
                )}
              </div>
              <button type="button" onClick={() => setShowDeliveryNotice(false)} className="mt-5 w-full min-h-[52px] rounded-2xl text-white font-bold" style={{ backgroundColor: primaryColor }}>
                Ho capito
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PWA Install Prompt */}
      <PushNotificationManager primaryColor={primaryColor} />
      
      {/* Header - Fixed with safe area for iPhone */}
      <header className="bg-white border-b border-[#E5E5E5] sticky top-0 z-50 pt-12 sm:pt-0" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 44px)' }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex justify-between items-center">
          <Link to={checkoutOnly ? "/ordina" : "/"} className="flex items-center gap-2">
            <ArrowLeft className="w-5 h-5" style={{ color: primaryColor }} />
            <Beef className="w-7 h-7" style={{ color: primaryColor }} />
            <span className="text-lg font-serif font-bold hidden sm:inline" style={{ color: primaryColor }}>
              {settings?.business_name || 'Macelleria Borgese'}
            </span>
          </Link>
          <div className="flex items-center gap-3">
            {/* Pulsante Aggiorna */}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
              title="Aggiorna prodotti"
            >
              <RefreshCw 
                className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} 
                style={{ color: primaryColor }} 
              />
            </button>
            {/* Carrello - Cliccabile su mobile */}
            <button
              onClick={() => navigate('/carrello')}
              className="lg:hidden flex items-center gap-2 text-white px-4 py-2 rounded-full"
              style={{ backgroundColor: primaryColor }}
            >
              <ShoppingBag className="w-5 h-5" />
              <span className="font-semibold">{cart.length}</span>
              {cartTotal > 0 && <span className="text-sm">€{(checkoutOnly ? finalTotal : roundMoney(cartTotal)).toFixed(2).replace('.', ',')}</span>}
            </button>
            {/* Carrello desktop */}
            <button
              type="button"
              onClick={() => navigate('/carrello')}
              className="hidden lg:flex items-center gap-2 text-white px-4 py-2 rounded-full"
              style={{ backgroundColor: primaryColor }}
            >
              <ShoppingBag className="w-5 h-5" />
              <span className="font-semibold">{cart.length}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Cart Drawer */}
      {showMobileCart && !checkoutOnly && (
        <div className="lg:hidden fixed inset-0 z-50">
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={() => setShowMobileCart(false)}
          />
          {/* Drawer */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[85vh] overflow-y-auto" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
            {/* Header */}
            <div className="sticky top-0 bg-white p-4 border-b border-[#E5E5E5] flex justify-between items-center">
              <h2 className="text-lg font-semibold text-[#2C2C2C] flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" style={{ color: primaryColor }} />
                Il Tuo Ordine
                {cart.length > 0 && (
                  <span className="text-sm font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: primaryColor }}>
                    {cart.length}
                  </span>
                )}
              </h2>
              <button onClick={() => setShowMobileCart(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            {cart.length === 0 ? (
              <div className="p-8 text-center">
                <ShoppingBag className="w-16 h-16 text-[#E5E5E5] mx-auto mb-4" />
                <p className="text-[#5A5A5A]">Il carrello è vuoto</p>
                <p className="text-[#9A9A9A] text-sm mt-2">Aggiungi prodotti per continuare</p>
                <button 
                  onClick={() => setShowMobileCart(false)}
                  className="mt-4 px-6 py-2 rounded-full text-white font-medium"
                  style={{ backgroundColor: primaryColor }}
                >
                  Sfoglia Prodotti
                </button>
              </div>
            ) : (
              <>
                {/* Cart Items */}
                <div className="p-4 space-y-3">
                  {cart.map(item => {
                    const product = products.find(p => p.id === item.product_id);
                    const itemTotal = (item.total_price || item.quantity * item.price_per_unit || 0);
                    return (
                      <div key={item.cart_item_id || item.product_id} className="flex items-center gap-3 p-3 bg-[#F9F7F2] rounded-xl">
                        <div className="flex-1">
                          <h4 className="font-medium text-[#2C2C2C]">{item.product_name}{item.variant_name ? ` — ${item.variant_name}` : ''}</h4>
                          <p className="text-sm text-[#5A5A5A]">€{(item.price_per_unit || 0).toFixed(2)}/{item.unit}</p>
                          <div className="mt-2" style={{ '--quantity-primary': primaryColor }}>
                            <EditableQuantity
                              item={item}
                              updateQuantity={updateQuantity}
                              changeQuantityByStep={changeQuantityByStep}
                              compact
                            />
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold" style={{ color: primaryColor }}>€{roundMoney(itemTotal).toFixed(2).replace('.', ',')}</p>
                          <button 
                            onClick={() => removeFromCart(item.cart_item_id || item.product_id)}
                            className="text-red-500 mt-2"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Total */}
                <div className="p-4 bg-[#F9F7F2] border-t border-[#E5E5E5]">
                  <div className="space-y-2 mb-2">
                      {customerInfo.fulfillment_type === 'delivery' && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-[#5A5A5A]">Totale prodotti</span>
                            <span className="font-semibold">€{roundMoney(cartTotal || 0).toFixed(2).replace('.', ',')}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-[#5A5A5A]">Consegna</span>
                            <span className="font-semibold">€{deliveryFee.toFixed(2).replace('.', ',')}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between items-center border-t border-[#E5E5E5] pt-2">
                        <span className="font-semibold text-[#2C2C2C]">Totale ordine</span>
                        <span className="text-xl font-bold" style={{ color: primaryColor }}>€{finalTotal.toFixed(2).replace('.', ',')}</span>
                      </div>
                    </div>
                  {cartTotal < minOrder && (
                    <p className="text-sm text-orange-600">Minimo €{minOrder.toFixed(0)} - Mancano €{roundMoney(minOrder - cartTotal).toFixed(2).replace('.', ',')}</p>
                  )}
                </div>

                {/* Customer Form */}
                <form noValidate onSubmit={(e) => { handleSubmit(e); setShowMobileCart(false); }} className="p-4 space-y-3">
                  <h3 className="font-semibold text-[#2C2C2C] flex items-center gap-2">
                    <User className="w-4 h-4" />
                    3. I tuoi dati
                  </h3>
                  
                  <input
                    type="text"
                    placeholder="Nome e Cognome *"
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 border border-[#E5E5E5] rounded-xl text-base"
                    required
                  />
                  
                  <input
                    type="tel"
                    placeholder="Telefono / WhatsApp *"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-4 py-3 border border-[#E5E5E5] rounded-xl text-base"
                    required
                  />
                  
                  <input
                    type="email"
                    placeholder="Email (opzionale)"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-3 border border-[#E5E5E5] rounded-xl text-base"
                  />

                  <h3 className="font-semibold text-[#2C2C2C] flex items-center gap-2 pt-2">
                    <Calendar className="w-4 h-4" />
                    Quando Ritiri?
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="date"
                      value={customerInfo.pickup_date}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, pickup_date: e.target.value, pickup_time: '' }))}
                      min={minDate}
                      className="w-full px-4 py-3 border border-[#E5E5E5] rounded-xl text-base"
                      required
                    />
                    <select
                      value={customerInfo.pickup_time}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, pickup_time: e.target.value }))}
                      className={`w-full px-4 py-3 border rounded-xl text-base ${availableTimes.length === 0 ? 'border-red-300 bg-red-50' : 'border-[#E5E5E5]'}`}
                      required
                      disabled={availableTimes.length === 0}
                    >
                      <option value="">Orario *</option>
                      {availableTimes.length === 0 ? (
                        <option value="" disabled>🔴 Chiuso</option>
                      ) : (
                        availableTimes.map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))
                      )}
                    </select>
                  </div>
                  
                  {availableTimes.length === 0 && customerInfo.pickup_date && (
                    <p className="text-sm text-red-600 bg-red-50 p-2 rounded-lg">
                      ⚠️ Il negozio è chiuso in questa data. Seleziona un altro giorno (Lun-Sab).
                    </p>
                  )}

                  <label className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={weightVariationAccepted}
                      onChange={(e) => setWeightVariationAccepted(e.target.checked)}
                      className="mt-1 h-5 w-5 flex-shrink-0 accent-amber-700"
                    />
                    <span className="text-sm leading-relaxed text-amber-950">
                      <strong className="block mb-1">Peso e prezzo possono variare</strong>
                      La quantità richiesta è indicativa. Poiché la carne non può avere sempre un peso preciso, il peso effettivo e il prezzo finale possono variare leggermente. Confermo di aver letto e accettato.
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={submitting || cart.length === 0}
                    className="w-full text-white py-4 rounded-xl font-semibold text-lg disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {submitting ? 'Invio...' : `Conferma ordine · €${finalTotal.toFixed(2).replace('.', ',')}`}
                    {!submitting && <ChevronRight className="w-5 h-5" />}
                  </button>

                  <button
                    type="button"
                    onClick={() => { clearCart(); toast.info('Carrello svuotato'); }}
                    className="w-full text-red-500 py-2 text-sm"
                  >
                    Svuota carrello
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <div className={`${checkoutOnly ? 'max-w-3xl' : 'max-w-5xl'} mx-auto px-3 sm:px-4 py-4 sm:py-6 pb-24`}>
        <div className="flex flex-col gap-4 sm:gap-6">
          {/* Catalogo mobile a step */}
          {!checkoutOnly && (
            <div>
              {!activeCategory ? (
                <>
                  <h1 className="text-2xl font-serif font-bold mb-2" style={{ color: primaryColor }}>Scegli una categoria</h1>
                  <div className="grid grid-cols-2 gap-3 mt-5">
                    {categories.map(category => {
                      const count = products.filter(product => normalizeCategory(product) === category.id).length;
                      return (
                        <button type="button" key={category.id}
                          onClick={() => { setActiveCategory(category.id); setSearchTerm(''); }}
                          className="min-h-[138px] rounded-3xl bg-white border border-[#E8E2D8] shadow-sm p-4 flex flex-col items-center justify-center text-center">
                          <span className="text-4xl">{category.icon || '📦'}</span>
                          <strong className="mt-3 text-lg" style={{ color: primaryColor }}>{category.name}</strong>
                          <span className="mt-1 text-xs text-[#746C64]">{count} prodotti</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <button type="button" onClick={() => { setActiveCategory(null); setSearchTerm(''); }}
                      className="w-11 h-11 rounded-xl bg-white border border-[#E8E2D8] flex items-center justify-center">
                      <ArrowLeft size={20} />
                    </button>
                    <h1 className="text-2xl font-serif font-bold" style={{ color: primaryColor }}>
                      {categories.find(item => item.id === activeCategory)?.name}
                    </h1>
                  </div>

                  <div className="relative mb-4">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8A8A8A]" />
                    <input type="search" value={searchTerm} onChange={event => setSearchTerm(event.target.value)}
                      placeholder="Cerca prodotto"
                      className="w-full pl-12 pr-4 py-3.5 bg-white border border-[#E5E5E5] rounded-2xl" />
                  </div>

                  {loading ? (
                    <div className="py-12 text-center">Caricamento prodotti…</div>
                  ) : (
                    <div className="space-y-2">
                      {filteredProducts.map(product => {
                        const variants = (product.variants || []).filter(item => item.available !== false);
                        const prices = [product.price_per_unit, ...variants.map(item => item.price_override).filter(value => value != null)].map(Number);
                        const minimumPrice = Math.min(...prices);
                        const different = new Set(prices.map(value => value.toFixed(2))).size > 1;
                        return (
                          <button type="button" key={product.id}
                            onClick={() => { setSelectedProduct(product); setProductAdded(false); }}
                            className="w-full min-h-[88px] bg-white rounded-2xl border border-[#E8E2D8] overflow-hidden flex items-center text-left shadow-sm">
                            <ProductImage product={product} primaryColor={primaryColor} showImages={showImages} />
                            <div className="min-w-0 flex-1 px-3 py-2">
                              <h2 className="font-bold text-sm sm:text-base truncate">{product.name}</h2>
                              <p className="mt-1 font-bold" style={{ color: primaryColor }}>
                                {different ? 'da ' : ''}€{minimumPrice.toFixed(2).replace('.', ',')}/{product.unit}
                              </p>
                            </div>
                            <ChevronRight className="w-5 h-5 mr-3 text-[#8C847A]" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {selectedProduct && !checkoutOnly && (
            <div className="fixed inset-0 z-50 bg-black/65 p-3 flex items-end sm:items-center justify-center">
              <div className="w-full max-w-md max-h-[92vh] overflow-y-auto bg-white rounded-t-3xl sm:rounded-3xl">
                <div className="sticky top-0 bg-white border-b border-[#E8E2D8] p-4 flex items-center justify-between">
                  <button type="button" onClick={() => { setSelectedProduct(null); setProductAdded(false); }}
                    className="w-10 h-10 rounded-xl bg-[#F2EEE7] flex items-center justify-center"><ArrowLeft size={20} /></button>
                  <strong>Prodotto</strong>
                  <button type="button" onClick={() => { setSelectedProduct(null); setProductAdded(false); }}
                    className="w-10 h-10 rounded-xl bg-[#F2EEE7] flex items-center justify-center"><X size={20} /></button>
                </div>

                <div className="p-4">
                  {selectedProduct.image_url ? (
                    <img src={selectedProduct.image_url} alt={selectedProduct.name}
                      className="w-full aspect-[4/3] object-cover rounded-2xl" />
                  ) : (
                    <div className="w-full aspect-[4/3] rounded-2xl flex items-center justify-center text-7xl"
                      style={{ backgroundColor: `${primaryColor}12` }}>
                      {categories.find(item => item.id === normalizeCategory(selectedProduct))?.icon || '📦'}
                    </div>
                  )}

                  <h2 className="mt-4 text-2xl font-serif font-bold">{selectedProduct.name}</h2>
                  <p className="mt-1 text-xl font-bold" style={{ color: primaryColor }}>
                    €{Number(selectedProduct.price_per_unit).toFixed(2).replace('.', ',')}/{selectedProduct.unit}
                  </p>

                  {(selectedProduct.variants || []).filter(item => item.available !== false).length > 0 && (
                    <div className="mt-5 grid gap-2">
                      {(selectedProduct.variants || []).filter(item => item.available !== false).map(variant => (
                        <button type="button" key={variant.id}
                          onClick={() => setSelectedVariants(previous => ({ ...previous, [selectedProduct.id]: variant.id }))}
                          className={`min-h-[50px] rounded-xl border-2 px-4 flex items-center justify-between font-semibold ${
                            selectedVariants[selectedProduct.id] === variant.id ? 'text-white' : 'bg-white border-[#DDD6CC]'
                          }`}
                          style={selectedVariants[selectedProduct.id] === variant.id ? { backgroundColor: primaryColor, borderColor: primaryColor } : {}}>
                          <span>{variant.name}</span>
                          <span>€{Number(variant.price_override ?? selectedProduct.price_per_unit).toFixed(2).replace('.', ',')}/{selectedProduct.unit}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {!productAdded ? (
                    <>
                      <div className="mt-5">
                        {(() => {
                          const variantId = selectedVariants[selectedProduct.id];
                          const key = `${selectedProduct.id}::${variantId || 'base'}`;
                          return <ProductQuantityPicker product={selectedProduct} draftKey={key}
                            value={productQuantityDrafts[key]}
                            onChange={(draftKey, value) => setProductQuantityDrafts(previous => ({ ...previous, [draftKey]: value }))}
                            primaryColor={primaryColor} />;
                        })()}
                      </div>
                      <button type="button"
                        onClick={() => { if (handleAddToCart(selectedProduct)) setProductAdded(true); }}
                        className="mt-5 w-full min-h-[56px] rounded-2xl text-white font-bold"
                        style={{ backgroundColor: primaryColor }}>
                        Aggiungi al carrello
                      </button>
                    </>
                  ) : (
                    <div className="mt-5 grid gap-3">
                      <div className="rounded-2xl bg-green-50 border border-green-200 p-4 text-center">
                        <Check className="w-8 h-8 text-green-700 mx-auto mb-2" />
                        <strong className="text-green-800">Aggiunto al carrello</strong>
                      </div>
                      <button type="button" onClick={() => { setSelectedProduct(null); navigate('/carrello'); }}
                        className="w-full min-h-[54px] rounded-2xl text-white font-bold"
                        style={{ backgroundColor: primaryColor }}>Vai al carrello</button>
                      <button type="button" onClick={() => { setSelectedProduct(null); setProductAdded(false); }}
                        className="w-full min-h-[52px] rounded-2xl border-2 font-bold"
                        style={{ borderColor: primaryColor, color: primaryColor }}>Ordina altro</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {checkoutOnly && (
            <div className="space-y-4">
              <div className="bg-white rounded-3xl shadow-sm border border-[#E8E2D8] overflow-hidden">
                <div className="p-4 sm:p-5 border-b border-[#E8E2D8]">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider" style={{ color: primaryColor }}>Checkout</p>
                      <h2 className="font-serif text-2xl font-bold text-[#2C2C2C]">Completa il tuo ordine</h2>
                    </div>
                    <span className="min-w-10 h-10 px-3 rounded-full text-white font-bold flex items-center justify-center" style={{ backgroundColor: primaryColor }}>{cart.length}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-5">
                    {[['1','Metodo'],['2','Orario'],['3','Dati'],['4','Conferma']].map(([number,label]) => (
                      <div key={number} className="text-center">
                        <div className="h-1.5 rounded-full mb-2" style={{ backgroundColor: primaryColor }} />
                        <span className="block text-[11px] sm:text-xs font-bold text-[#5A5A5A]">{number}. {label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {cart.length === 0 ? (
                  <div className="p-8 text-center">
                    <ShoppingBag className="w-14 h-14 text-[#D8D2C8] mx-auto mb-4" />
                    <h3 className="font-bold text-lg">Il carrello è vuoto</h3>
                    <p className="text-sm text-[#6B655D] mt-1">Aggiungi almeno un prodotto per continuare.</p>
                    <button type="button" onClick={() => navigate('/ordina')} className="mt-5 min-h-[50px] px-6 rounded-2xl text-white font-bold" style={{ backgroundColor: primaryColor }}>
                      Scegli i prodotti
                    </button>
                  </div>
                ) : (
                  <form noValidate onSubmit={handleSubmit}>
                    <section className="p-4 sm:p-5 border-b border-[#E8E2D8]">
                      <h3 className="font-bold text-lg mb-3">1. Come vuoi ricevere l’ordine?</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <button type="button" onClick={() => { setDeliveryValidation({ checking:false, valid:false, message:'', formattedAddress:'' }); setCustomerInfo(p => ({ ...p, fulfillment_type:'pickup', pickup_date:'', pickup_time:'' })); }}
                          className={`min-h-[76px] rounded-2xl border-2 px-3 py-3 flex flex-col sm:flex-row items-center justify-center gap-2 font-bold ${customerInfo.fulfillment_type === 'pickup' ? 'text-white' : 'bg-white border-[#D8D2C8]'}`}
                          style={customerInfo.fulfillment_type === 'pickup' ? { backgroundColor:primaryColor, borderColor:primaryColor } : {}}>
                          <Store className="w-6 h-6" /><span>Ritiro</span>
                        </button>
                        <button type="button" disabled={!settings?.delivery_enabled} onClick={() => { setDeliveryValidation({ checking:false, valid:false, message:'', formattedAddress:'' }); setCustomerInfo(p => ({ ...p, fulfillment_type:'delivery', pickup_date:'', pickup_time:'' })); setShowDeliveryNotice(true); }}
                          className={`min-h-[76px] rounded-2xl border-2 px-3 py-3 flex flex-col sm:flex-row items-center justify-center gap-2 font-bold disabled:opacity-40 ${customerInfo.fulfillment_type === 'delivery' ? 'text-white' : 'bg-white border-[#D8D2C8]'}`}
                          style={customerInfo.fulfillment_type === 'delivery' ? { backgroundColor:primaryColor, borderColor:primaryColor } : {}}>
                          <Truck className="w-6 h-6" /><span>Consegna</span>
                        </button>
                      </div>
                      {!settings?.delivery_enabled && <p className="text-xs text-[#6B655D] mt-2">La consegna a domicilio al momento non è disponibile.</p>}
                    </section>

                    {customerInfo.fulfillment_type === 'delivery' && (
                      <section className="p-4 sm:p-5 border-b border-[#E8E2D8] bg-blue-50/70">
                        <h3 className="font-bold text-lg flex items-center gap-2"><MapPin className="w-5 h-5 text-blue-700" />Indirizzo di consegna</h3>
                        <p className="mt-1 text-sm text-blue-900">La consegna è disponibile esclusivamente nei paesi attivi indicati qui sotto.</p>
                        {activeDeliveryZones.length > 0 && (
                          <label className="block mt-3">
                            <span className="block text-xs font-bold text-blue-900 mb-1.5">PAESE DI CONSEGNA *</span>
                            <select value={customerInfo.delivery_zone_id} onChange={e => { setDeliveryValidation({ checking:false, valid:false, message:'', formattedAddress:'' }); setCustomerInfo(p => ({ ...p, delivery_zone_id: e.target.value })); }} className="w-full min-h-[50px] px-4 border border-blue-200 rounded-xl bg-white" required>
                              <option value="">Seleziona il paese</option>
                              {activeDeliveryZones.map(zone => <option key={zone.id} value={zone.id}>{zone.name} — €{Number(zone.fee || 0).toFixed(2).replace('.', ',')}</option>)}
                            </select>
                          </label>
                        )}
                        <input type="text" placeholder="Via, numero civico e comune *" value={customerInfo.delivery_address}
                          onChange={e => { setDeliveryValidation({ checking:false, valid:false, message:'', formattedAddress:'' }); setCustomerInfo(p => ({ ...p, delivery_address:e.target.value })); }}
                          className="mt-3 w-full min-h-[50px] px-4 border border-blue-200 rounded-xl bg-white" required />
                        <button type="button" onClick={() => validateDeliveryAddress()} disabled={deliveryValidation.checking || !customerInfo.delivery_address.trim() || (activeDeliveryZones.length > 0 && !selectedDeliveryZone)}
                          className="mt-3 w-full min-h-[48px] rounded-xl bg-blue-700 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                          {deliveryValidation.checking ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
                          {deliveryValidation.checking ? 'Controllo indirizzo…' : 'Verifica indirizzo'}
                        </button>
                        {deliveryValidation.message && <p className={`mt-2 text-sm font-semibold ${deliveryValidation.valid ? 'text-green-700' : 'text-red-700'}`}>{deliveryValidation.message}</p>}
                        <input type="text" placeholder="Citofono, piano o indicazioni" value={customerInfo.delivery_notes}
                          onChange={e => setCustomerInfo(p => ({ ...p, delivery_notes:e.target.value }))}
                          className="mt-3 w-full min-h-[48px] px-4 border border-blue-200 rounded-xl bg-white" />
                      </section>
                    )}

                    <section className="p-4 sm:p-5 border-b border-[#E8E2D8]">
                      <h3 className="font-bold text-lg flex items-center gap-2"><Calendar className="w-5 h-5" />2. Scegli giorno e orario</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                        <label><span className="block text-xs font-bold text-[#6B655D] mb-1.5">GIORNO</span>
                          <input type="date" value={customerInfo.pickup_date} onChange={e => setCustomerInfo(p => ({ ...p, pickup_date:e.target.value, pickup_time:'' }))}
                            min={minDate} className="w-full min-h-[50px] px-4 border border-[#D8D2C8] rounded-xl bg-white" required />
                        </label>
                        <label><span className="block text-xs font-bold text-[#6B655D] mb-1.5">ORARIO</span>
                          <select value={customerInfo.pickup_time} onChange={e => setCustomerInfo(p => ({ ...p, pickup_time:e.target.value }))}
                            className={`w-full min-h-[50px] px-4 border rounded-xl bg-white ${customerInfo.pickup_date && availableTimes.length === 0 ? 'border-red-300' : 'border-[#D8D2C8]'}`}
                            required disabled={!customerInfo.pickup_date || availableTimes.length === 0}>
                            <option value="">Scegli orario</option>
                            {availableTimes.map(time => <option key={time} value={time}>{time}</option>)}
                          </select>
                        </label>
                      </div>
                      {customerInfo.pickup_date && availableTimes.length === 0 && <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">In questo giorno non sono disponibili orari. Scegli un’altra data.</p>}
                    </section>

                    <section className="p-4 sm:p-5 border-b border-[#E8E2D8]">
                      <h3 className="font-bold text-lg flex items-center gap-2"><User className="w-5 h-5" />3. I tuoi dati</h3>
                      <div className="grid gap-3 mt-3">
                        <input type="text" placeholder="Nome e cognome *" value={customerInfo.name} onChange={e => setCustomerInfo(p => ({ ...p, name:e.target.value }))} className="w-full min-h-[50px] px-4 border border-[#D8D2C8] rounded-xl" required />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <input type="tel" placeholder="Telefono *" value={customerInfo.phone} onChange={e => setCustomerInfo(p => ({ ...p, phone:e.target.value }))} className="w-full min-h-[50px] px-4 border border-[#D8D2C8] rounded-xl" required />
                          <input type="email" placeholder="Email" value={customerInfo.email} onChange={e => setCustomerInfo(p => ({ ...p, email:e.target.value }))} className="w-full min-h-[50px] px-4 border border-[#D8D2C8] rounded-xl" />
                        </div>
                        {showNotes && <textarea placeholder="Note generali per l’ordine" value={customerInfo.notes} onChange={e => setCustomerInfo(p => ({ ...p, notes:e.target.value }))} rows="2" className="w-full px-4 py-3 border border-[#D8D2C8] rounded-xl resize-none" />}
                      </div>
                    </section>

                    <section className="p-4 sm:p-5">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <h3 className="font-bold text-lg flex items-center gap-2"><ShoppingBag className="w-5 h-5" />4. Riepilogo</h3>
                        <button type="button" onClick={() => navigate('/ordina')} className="text-sm font-bold" style={{ color:primaryColor }}>Modifica prodotti</button>
                      </div>
                      <div className="rounded-2xl border border-[#E8E2D8] overflow-hidden">
                        {cart.map(item => {
                          const itemTotal = item.total_price || item.quantity * item.price_per_unit || 0;
                          return (
                            <div key={item.cart_item_id || item.product_id} className="p-3 sm:p-4 border-b border-[#E8E2D8] last:border-b-0">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <h4 className="font-bold truncate">{item.product_name}{item.variant_name ? ` — ${item.variant_name}` : ''}</h4>
                                  <p className="text-xs text-[#6B655D] mt-0.5">€{Number(item.price_per_unit || 0).toFixed(2).replace('.', ',')}/{item.unit}</p>
                                  <div className="mt-2" style={{ '--quantity-primary':primaryColor }}>
                                    <EditableQuantity item={item} updateQuantity={updateQuantity} changeQuantityByStep={changeQuantityByStep} compact />
                                  </div>
                                </div>
                                <div className="text-right">
                                  <strong style={{ color:primaryColor }}>€{roundMoney(itemTotal).toFixed(2).replace('.', ',')}</strong>
                                  <button type="button" onClick={() => removeFromCart(item.cart_item_id || item.product_id)} className="block ml-auto mt-2 text-red-600"><Trash2 className="w-4 h-4" /></button>
                                </div>
                              </div>
                              <input type="text" placeholder="Nota per questo prodotto, es. taglio sottile" value={item.notes || ''} onChange={e => updateItemNotes(item.cart_item_id || item.product_id, e.target.value)} className="mt-3 w-full min-h-[42px] px-3 text-sm border border-[#E8E2D8] rounded-xl" />
                            </div>
                          );
                        })}
                      </div>

                      <div className="mt-4 rounded-2xl bg-[#F9F7F2] border border-[#E8E2D8] p-4 space-y-2">
                        <div className="flex justify-between text-sm"><span className="text-[#6B655D]">Prodotti</span><strong>€{roundMoney(cartTotal || 0).toFixed(2).replace('.', ',')}</strong></div>
                        {customerInfo.fulfillment_type === 'delivery' && <div className="flex justify-between text-sm"><span className="text-[#6B655D]">Consegna</span><strong>€{deliveryFee.toFixed(2).replace('.', ',')}</strong></div>}
                        <div className="flex justify-between items-center border-t border-[#DDD6CC] pt-3 mt-3"><span className="font-bold">Totale ordine</span><strong className="text-2xl" style={{ color:primaryColor }}>€{finalTotal.toFixed(2).replace('.', ',')}</strong></div>
                      </div>

                      {cartTotal < minOrder && <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3 text-center"><p className="text-red-700 text-sm font-bold">Aggiungi ancora €{roundMoney(minOrder - cartTotal).toFixed(2).replace('.', ',')} per raggiungere il minimo di €{Number(minOrder).toFixed(2).replace('.', ',')}</p></div>}

                      <label className="mt-4 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={weightVariationAccepted}
                          onChange={(e) => setWeightVariationAccepted(e.target.checked)}
                          className="mt-1 h-5 w-5 flex-shrink-0 accent-amber-700"
                        />
                        <span className="text-sm leading-relaxed text-amber-950">
                          <strong className="block mb-1 text-base">Peso e prezzo possono variare</strong>
                          La quantità richiesta è indicativa. Poiché la carne non può avere sempre un peso preciso, il peso effettivo e il prezzo finale possono variare leggermente. Confermo di aver letto e accettato.
                        </span>
                      </label>

                      <button type="submit" disabled={submitting || cart.length === 0}
                        className="mt-4 w-full min-h-[58px] rounded-2xl text-white font-bold text-base flex items-center justify-center gap-2 disabled:opacity-50"
                        style={{ backgroundColor:primaryColor }}>
                        {submitting ? <><Loader2 className="w-5 h-5 animate-spin" />Invio ordine…</> : <>Conferma ordine · €{finalTotal.toFixed(2).replace('.', ',')}<ChevronRight className="w-5 h-5" /></>}
                      </button>
                      <p className="mt-3 text-center text-xs text-[#6B655D]">L’ordine sarà confermato dalla macelleria dopo l’invio.</p>
                    </section>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Fixed Bottom Cart Button */}
      {cart.length > 0 && !checkoutOnly && (
        <div className="fixed bottom-0 left-0 right-0 p-3 bg-white border-t border-[#E5E5E5] shadow-lg z-40" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
          <button
            type="button"
            onClick={() => navigate('/carrello')}
            className="max-w-3xl mx-auto w-full text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-3"
            style={{ backgroundColor: primaryColor }}
          >
            <ShoppingBag className="w-5 h-5" />
            <span>Vai al carrello</span>
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
              {cart.length} • €{roundMoney(cartTotal).toFixed(2).replace('.', ',')}
            </span>
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}
